import { BellRing, Check, ChevronRight, ExternalLink, NotebookPen, UserPlus, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api, isApiError } from '@/api';
import type { DossierQueueDetail } from '@/api/areas/dossierQueue';
import { canApprove, IDENTITY_KINDS, isRequestKind, isReviewKind, type WorkItem } from '@/api/areas/work';
import { Badge } from '@/components/Badge';
import { Callout, Field } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { KeyValue } from '@/components/KeyValue';
import { Loading } from '@/components/States';
import { SystemReason } from '@/components/SystemReason';
import { useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { formatDateTime, formatRelative } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { ActionDialog, FormActions } from '../compliance/ui';
import { DeclarationReviewPanel } from '../declarations/DeclarationReviewPanel';
import { DOSSIER_STATUS, EnumBadge, MissingItems } from '../accounts/shared';
import { FileViewer, useCheckLabel, type FileTarget } from '../declarations/shared';
import { StageBadge, useFindingText, WaitAndDeadline } from './dossierShared';
import { HstmPanel } from './HstmPanel';
import { AssignForm, DecisionForm, FieldResults } from './WorkItemPage';

type Dialog = 'approve' | 'reject' | 'assign' | 'note' | 'remind' | null;


function Block({ title, children, hint }: { title: ReactNode; children: ReactNode; hint?: ReactNode }) {
  return (
    <section className="grid gap-2 border-t pt-4">
      <h3 className="text-sm font-semibold">{title}</h3>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {children}
    </section>
  );
}

/** "Nhắc khách": e-mail the registrant (once per 24 h per dossier). */
export function RemindForm({ dossierId, domain, onDone, onCancel }: { dossierId: string; domain: string; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('dossiers');
  const { t: tc } = useTranslation();
  const [note, setNote] = useState('');
  const write = useWrite((body: { note?: string }, opts) => api.remindDossier(dossierId, body, opts));
  const err = write.state.phase === 'error' ? write.state.error : null;
  const tooSoon = err && isApiError(err) && err.code === 'REMINDER_TOO_SOON';
  const nextAt = tooSoon && isApiError(err) ? ((err.details as { next_at?: string } | undefined)?.next_at ?? null) : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (await write.run({ note: note.trim() || undefined })) onDone();
      }}
    >
      <p className="text-sm text-muted-foreground">{t('remind.description', { domain })}</p>
      <Field label={t('remind.note')} hint={t('remind.noteHint')}>
        <Textarea rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} disabled={write.busy} />
      </Field>
      {tooSoon ? <Callout tone="warn">{t('remind.tooSoon', { when: nextAt ? formatRelative(nextAt) : '' })}</Callout> : err ? <WriteError error={err} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy}>
          <BellRing /> {t('remind.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

function NoteForm({ dossierId, onDone, onCancel }: { dossierId: string; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('dossiers');
  const { t: tc } = useTranslation();
  const [body, setBody] = useState('');
  const write = useWrite((b: { body: string }, opts) => api.addDossierNote(dossierId, b, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (body.trim() && (await write.run({ body: body.trim() }))) onDone();
      }}
    >
      <Field label={t('action.note')} hint={t('panel.noteHint')}>
        <Textarea rows={3} maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} disabled={write.busy} />
      </Field>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !body.trim()}>
          {tc('action.save')}
        </Button>
      </FormActions>
    </form>
  );
}

function Facts({ d }: { d: DossierQueueDetail }) {
  const { t } = useTranslation('dossiers');
  const { t: tw } = useTranslation('work');
  const { t: td } = useTranslation('declarations');
  const { t: ta } = useTranslation('accounts');
  const [viewing, setViewing] = useState<(FileTarget & { documentId: string; version: string }) | null>(null);
  const checkLabel = useCheckLabel();
  const reg = d.registrant;
  return (
    <>
      {reg ? (
        <Block title={t('panel.registrant', { version: reg.version_number })}>
          {reg.current_version_id && reg.current_version_id !== reg.subject_version_id ? <Callout tone="warn">{t('panel.newerVersion')}</Callout> : null}
          <KeyValue
            columns={2}
            rows={[
              [t('col.registrant'), <span key="n" className="font-medium">{reg.legal_name ?? '—'}</span>],
              [t('panel.subjectType'), t(`subjectType.${reg.subject_type as 'INDIVIDUAL'}`, { defaultValue: reg.subject_type })],
              [
                t('panel.identifiers'),
                reg.identifiers.length ? (
                  <span key="i" className="grid gap-0.5">
                    {reg.identifiers.map((i) => (
                      <code key={`${i.type}-${i.hint}`} className="text-xs">
                        {i.type} · {i.country} · {i.hint}
                      </code>
                    ))}
                  </span>
                ) : (
                  '—'
                ),
              ],
              [t('panel.emails'), reg.emails?.join(', ') || '—'],
            ]}
          />
          <Link to={`/subjects/${encodeURIComponent(reg.subject_id)}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
            {t('panel.openSubject')} <ExternalLink className="size-3.5" aria-hidden="true" />
          </Link>
        </Block>
      ) : null}
      <Block title={t('panel.verifications')}>
        {d.verifications.length ? (
          <ul className="grid gap-1.5 text-sm">
            {d.verifications.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center gap-2">
                <Badge tone={v.status === 'VERIFIED' ? 'ok' : v.status === 'PENDING' ? 'info' : 'warn'}>{v.status}</Badge>
                <span className="font-medium">{v.method}</span>
                {v.provider ? <span className="text-muted-foreground">{v.provider}</span> : null}
                <span className="text-muted-foreground" title={formatDateTime(v.created_at)}>
                  {formatRelative(v.created_at)}
                </span>
                {v.reviewer_name ? <span className="text-muted-foreground">· {v.reviewer_name}</span> : null}
                {v.pinned ? <Badge tone="ok">{t('panel.pinned')}</Badge> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{t('panel.noVerifications')}</p>
        )}
      </Block>
      {d.declarations.length ? (
        <Block title={t('panel.declarations')}>
          {d.declarations.map((x) => (
            <div key={x.submission_id} className="grid gap-2 rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge tone={x.status === 'VERIFIED' || x.status === 'APPROVED' ? 'ok' : x.status === 'REJECTED' ? 'crit' : 'warn'}>{x.status}</Badge>
                <span className="font-medium">{x.upload_kind === 'SCAN' ? t('method.DECLARATION_SCAN') : x.upload_kind === 'SIGNED_PDF' ? t('method.DECLARATION_SIGNED') : t('method.DECLARATION')}</span>
                <code className="text-xs text-muted-foreground">{x.template}</code>
                <span className="text-muted-foreground">{formatRelative(x.uploaded_at)}</span>
              </div>
              {x.checks.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <caption className="sr-only">{t('panel.checks')}</caption>
                    <tbody>
                      {x.checks.map((c) => (
                        <tr key={c.code} className="border-t first:border-t-0">
                          <td className="py-1 pr-2" title={c.code}>
                            {checkLabel(c)}
                          </td>
                          <td className="py-1 pr-2">
                            <Badge tone={c.status === 'PASS' ? 'ok' : c.status === 'FAIL' ? 'crit' : 'warn'} title={c.status}>
                              {td(`checkStatus.${c.status}`)}
                            </Badge>
                          </td>
                          <td className="py-1 text-muted-foreground">{[c.reason, c.message].filter(Boolean).join(' · ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ))}
        </Block>
      ) : null}
      {d.documents.length ? (
        <Block title={t('panel.documents')} hint={ta('document.readBody')}>
          <ul className="grid gap-1 text-sm">
            {d.documents.map((doc) => (
              <li key={doc.document_version_id}>
                <button type="button" onClick={() => setViewing({ name: doc.purpose, documentId: doc.document_id, version: doc.document_version_id, download: false })} className="text-left text-primary hover:underline">
                  {doc.purpose}
                </button>{' '}
                <span className="text-muted-foreground">{formatRelative(doc.bound_at)}</span>
              </li>
            ))}
          </ul>
        </Block>
      ) : null}
      <FileViewer
        target={viewing}
        onClose={() => setViewing(null)}
        title={ta('document.readTitle', { name: viewing?.name ?? '' })}
        description={ta('document.readBody')}
        hint={ta('document.previewHint')}
        downloadLabel={ta('document.download')}
        marker="dossier-document-preview"
        load={(reason, f) => api.getDocumentContent(viewing?.documentId ?? '', viewing?.version ?? '', { reason, download: f.download })}
      />
      {d.work_items.length ? (
        <Block title={t('panel.items')}>
          <ul className="grid gap-1 text-sm">
            {d.work_items.map((w) => (
              <li key={w.id} className="flex flex-wrap items-center gap-2">
                <Badge tone={w.status === 'OPEN' ? 'pending' : 'neutral'}>{w.status === 'OPEN' ? tw('status.OPEN') : tw(`resolution.${(w.resolution ?? 'DONE') as 'DONE'}`, { defaultValue: w.resolution ?? '' })}</Badge>
                <Link to={`/compliance/work/${encodeURIComponent(w.id)}`} className="text-primary hover:underline">
                  {tw(`kind.${w.kind as 'EKYC_MISMATCH'}`, { defaultValue: w.kind })}
                </Link>
                <span className="text-muted-foreground">{formatRelative(w.opened_at)}</span>
                {w.resolution_reason ? (
                  <span className="text-muted-foreground">
                    · <SystemReason reason={w.resolution_reason} />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </Block>
      ) : null}
      <Block title={t('panel.reminders')}>
        {d.reminders.length || d.notes.length ? (
          <ul className="grid gap-1.5 text-sm">
            {[
              ...d.reminders.map((r) => ({ at: r.sent_at, key: r.id, text: `${t('action.remind')} · ${r.sent_by_name ?? ''}${r.note ? ` · “${r.note}”` : ''}`, icon: <BellRing className="size-3.5" aria-hidden="true" /> })),
              ...d.notes.map((n) => ({ at: n.created_at, key: n.id, text: `${n.created_by_name ?? ''}: ${n.body}`, icon: <NotebookPen className="size-3.5" aria-hidden="true" /> })),
            ]
              .sort((a, b) => b.at.localeCompare(a.at))
              .map((e) => (
                <li key={e.key} className="flex items-start gap-2">
                  <span className="mt-0.5 text-muted-foreground">{e.icon}</span>
                  <span className="min-w-0 break-words">
                    <span className="text-muted-foreground" title={formatDateTime(e.at)}>
                      {formatRelative(e.at)}
                    </span>{' '}
                    {e.text}
                  </span>
                </li>
              ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{t('panel.noReminders')}</p>
        )}
      </Block>
      {d.history.length ? (
        <Block title={t('panel.history')}>
          <ul className="grid gap-1 text-sm">
            {d.history.map((h) => (
              <li key={`${h.at}-${h.to}`} className="flex flex-wrap gap-2">
                <span className="text-muted-foreground" title={formatDateTime(h.at)}>
                  {formatRelative(h.at)}
                </span>
                <span className="flex flex-wrap items-center gap-1">
                  {h.from ? <EnumBadge group="dossierStatus" tones={DOSSIER_STATUS} value={h.from} /> : '—'} → <EnumBadge group="dossierStatus" tones={DOSSIER_STATUS} value={h.to} />
                </span>
                {h.missing.length ? <span className="text-xs text-muted-foreground"><MissingItems items={h.missing} /></span> : null}
                {h.reason ? (
                  <span className="text-muted-foreground">
                    <SystemReason reason={h.reason} />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </Block>
      ) : null}
    </>
  );
}

/**
 * The review panel of one dossier (side sheet): the domain and order, what the system found, the
 * registrant subject version (masked identifiers), verifications, declaration checks, documents,
 * history; and the decision through the dossier's review item (approve / request correction —
 * the work item's resolve endpoint, never a separate path), assignment, notes and reminders.
 * Keys: A approve, R request correction, N next item.
 */
export function DossierReviewSheet({ dossierId, onClose, onChanged, onNext, hasNext }: { dossierId: string; onClose: () => void; onChanged: () => void; onNext: () => void; hasNext: boolean }) {
  const { t } = useTranslation('dossiers');
  const { t: tw } = useTranslation('work');
  const detail = useApi((signal) => api.getDossierQueueItem(dossierId, { signal }), [dossierId]);
  const itemId = detail.status === 'success' ? (detail.data.review_item_id ?? detail.data.warning_item_id) : null;
  const item = useApi<WorkItem | null>((signal) => (itemId ? api.getWorkItem(itemId, { signal }) : Promise.resolve(null)), [itemId]);
  const [dialog, setDialog] = useState<Dialog>(null);
  // "Decision recorded" belongs to the dossier it was made on.
  const [decidedOn, setDecidedOn] = useState<string | null>(null);
  const decided = decidedOn === dossierId;
  const takeMe = useWrite((_: null, opts) => (itemId ? api.assignWorkItem(itemId, {}, opts) : Promise.reject(new Error('no item'))));
  const findingText = useFindingText();
  const w = item.status === 'success' ? item.data : null;
  const canDecide = Boolean(w && w.status === 'OPEN' && isReviewKind(w.kind) && !isRequestKind(w.kind));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (dialog || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('input,textarea,select,button,a,[contenteditable="true"]')) return;
      const modal = target?.closest('[role="dialog"]');
      if (modal && !modal.hasAttribute('data-dossier-review')) return;
      if (e.key === 'a' && canDecide && w && canApprove(w)) {
        e.preventDefault();
        setDialog('approve');
      } else if (e.key === 'r' && canDecide) {
        e.preventDefault();
        setDialog('reject');
      } else if (e.key === 'n' && hasNext) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, canDecide, hasNext, onNext, w]);

  const refresh = () => {
    detail.reload();
    item.reload();
    onChanged();
  };
  const afterDecision = () => {
    setDialog(null);
    setDecidedOn(dossierId);
    onChanged();
    if (hasNext) onNext();
    else refresh();
  };

  const d = detail.status === 'success' ? detail.data : null;
  const finding = d ? findingText(d) : null;
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-2xl" data-dossier-review>
        <SheetHeader className="shrink-0 border-b">
          <SheetTitle className="flex flex-wrap items-center gap-2 pr-6">
            {t('panel.title')}
            {d ? <StageBadge stage={d.stage} /> : null}
          </SheetTitle>
          <SheetDescription className="break-all">{d ? d.unicode_name : ''}</SheetDescription>
        </SheetHeader>
        {d ? (
          <div className="shrink-0 space-y-2 border-b bg-background p-4" data-dossier-actions>
              <div className="flex flex-wrap gap-2">
                {canDecide ? (
                  <>
                    {w && canApprove(w) ? (
                      <Button size="sm" onClick={() => setDialog('approve')} aria-keyshortcuts="a">
                        <Check /> {t('action.approve')}
                      </Button>
                    ) : null}
                    <Button size="sm" variant="destructive" onClick={() => setDialog('reject')} aria-keyshortcuts="r">
                      <X /> {t('action.reject')}
                    </Button>
                  </>
                ) : null}
                {w && w.status === 'OPEN' ? (
                  <>
                    <Button size="sm" variant="outline" disabled={takeMe.busy} onClick={async () => (await takeMe.run(null)) && refresh()}>
                      <UserPlus /> {t('action.assignMe')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDialog('assign')}>
                      {t('action.assignOther')}
                    </Button>
                  </>
                ) : null}
                {d.stage === 'AWAITING_CUSTOMER' ? (
                  <Button size="sm" variant={canDecide ? 'outline' : 'default'} onClick={() => setDialog('remind')}>
                    <BellRing /> {t('action.remind')}
                  </Button>
                ) : null}
                <Button size="sm" variant="outline" onClick={() => setDialog('note')}>
                  <NotebookPen /> {t('action.note')}
                </Button>
                {w ? (
                  <Button size="sm" variant="ghost" asChild>
                    <Link to={`/compliance/work/${encodeURIComponent(w.id)}`}>
                      <ExternalLink /> {t('panel.openItem')}
                    </Link>
                  </Button>
                ) : null}
                {hasNext ? (
                  <Button size="sm" variant="ghost" onClick={onNext} aria-keyshortcuts="n">
                    {t('action.next')} <ChevronRight />
                  </Button>
                ) : null}
              </div>
              {takeMe.state.phase === 'error' ? <WriteError error={takeMe.state.error} /> : null}
              {canDecide ? <p className="text-xs text-muted-foreground">{t('panel.decisionHint')}</p> : null}
          </div>
        ) : null}
        <div key={dossierId} className="grid min-h-0 flex-1 content-start gap-4 overflow-y-auto p-4">
          {detail.status === 'loading' ? <Loading rows={6} /> : null}
          {detail.status === 'error' ? <DataError error={detail.error} onRetry={detail.reload} title={t('panel.loadError')} /> : null}
          {decided && !hasNext ? <Callout tone="ok">{t('panel.lastItem')}</Callout> : null}
          {d && finding ? (
            <>
              <KeyValue
                columns={2}
                rows={[
                  [
                    t('col.domain'),
                    <Link key="d" to={`/domains/${encodeURIComponent(d.domain_name)}`} className="font-medium text-primary hover:underline">
                      {d.unicode_name}
                    </Link>,
                  ],
                  [t('panel.operation'), t(`operation.${d.operation ?? 'none'}`)],
                  [
                    t('col.customer'),
                    <span key="c">
                      <Link to={`/customers/${encodeURIComponent(d.customer_id)}`} className="text-primary hover:underline">
                        {d.customer_name ?? d.customer_id}
                      </Link>
                    </span>,
                  ],
                  d.order_id
                    ? [
                        t('panel.order'),
                        <Link key="o" to={`/orders/${encodeURIComponent(d.order_id)}`} className="text-primary hover:underline">
                          {t('panel.openOrder')}
                        </Link>,
                      ]
                    : null,
                  [t('panel.dossier'), `${d.dossier_status} · v${d.dossier_version}`],
                  [t('col.method'), d.method ? t(`method.${d.method}`) : t('method.none')],
                  [t('col.waiting'), <WaitAndDeadline key="w" row={d} />],
                  [t('col.assignee'), d.assignee_name ?? '—'],
                ]}
              />
              <Callout tone={finding.tone} title={finding.headline}>
                {finding.reasons.length ? (
                  <ul className="list-disc pl-4">
                    {finding.reasons.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                ) : null}
                {d.deadline_paused ? <p className="text-xs">{t('deadline.paused')}</p> : null}
              </Callout>
              {w && isRequestKind(w.kind) ? <Callout tone="neutral">{t('panel.requestItem')}</Callout> : null}
              {d.last_reminder_at ? <p className="text-xs text-muted-foreground">{t('remind.lastSent', { when: formatRelative(d.last_reminder_at), count: d.reminders_sent })}</p> : null}
              <p className="hidden text-xs text-muted-foreground sm:block">{t('panel.keyboard')}</p>
              {w && IDENTITY_KINDS.includes(w.kind) ? <FieldResults item={w} /> : null}
              {w && w.kind === 'DECLARATION_REVIEW' ? <DeclarationReviewPanel item={w} /> : null}
              <Facts key={d.dossier_id} d={d} />
              {d.hstm ? <HstmPanel dossierId={d.dossier_id} plan={d.hstm} onChanged={refresh} /> : null}
            </>
          ) : null}
        </div>
        {d && w ? (
          <>
            <ActionDialog open={dialog === 'approve'} onOpenChange={(o) => setDialog(o ? 'approve' : null)} title={tw('approve.title')} description={t('panel.decisionHint')}>
              <DecisionForm item={w} dialog="approve" onDone={afterDecision} onCancel={() => setDialog(null)} />
            </ActionDialog>
            <ActionDialog open={dialog === 'reject'} onOpenChange={(o) => setDialog(o ? 'reject' : null)} title={tw('reject.title')} description={w.dossier_id ? tw('reject.description') : tw('reject.descriptionNoDossier')}>
              <DecisionForm item={w} dialog="reject" onDone={afterDecision} onCancel={() => setDialog(null)} />
            </ActionDialog>
            <ActionDialog open={dialog === 'assign'} onOpenChange={(o) => setDialog(o ? 'assign' : null)} title={tw('assign.title')}>
              <AssignForm
                item={w}
                onDone={() => {
                  setDialog(null);
                  refresh();
                }}
                onCancel={() => setDialog(null)}
              />
            </ActionDialog>
          </>
        ) : null}
        {d ? (
          <>
            <ActionDialog open={dialog === 'remind'} onOpenChange={(o) => setDialog(o ? 'remind' : null)} title={t('remind.title')}>
              <RemindForm
                dossierId={d.dossier_id}
                domain={d.unicode_name}
                onDone={() => {
                  setDialog(null);
                  refresh();
                }}
                onCancel={() => setDialog(null)}
              />
            </ActionDialog>
            <ActionDialog open={dialog === 'note'} onOpenChange={(o) => setDialog(o ? 'note' : null)} title={t('panel.noteTitle')}>
              <NoteForm
                dossierId={d.dossier_id}
                onDone={() => {
                  setDialog(null);
                  detail.reload();
                }}
                onCancel={() => setDialog(null)}
              />
            </ActionDialog>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
